import { HttpEvent, HttpEventType } from "@angular/common/http";
import { Observable, Observer, Subscription } from "rxjs";
import { ICommand } from "../../modules/records/commands/i-command";
import { RecordsManager } from "../../modules/records/records-manager";
import { DownloadLogService } from "src/app/services/download-log.service";
import { MyUtils } from "src/app/services/my-utils.service";
import { IPayload } from "src/app/interface/filters";

export class UploadExcelCommand<
  T extends object & { isDisabled?: boolean },
  S extends {
    upload: (file: File, additionalData: Map<string, string> | undefined) => Observable<HttpEvent<any>>;
  }
> implements ICommand<T, S> {
  private receiver!: RecordsManager<T>;
  type: "primary" | "warn" | "accent" = "accent";
  label = "uploadExcel";
  icon = "publish";
  layout: "flat" | undefined;
  isLoading = false;
  isElastic: boolean = false;
  filter: any = null;
  
  private fileInput: HTMLInputElement | null = null;
  private downloadLogService: DownloadLogService | null = null;

  private observer: Observer<HttpEvent<any>> = {
    next: (event: HttpEvent<any>) => {
      if (event.type === HttpEventType.UploadProgress) {
        const percentDone = event.total ? Math.round(100 * event.loaded / event.total) : 0;
        console.log(`File upload progress: ${percentDone}%`);
      } else if (event.type === HttpEventType.Response) {
        console.log('File upload complete!', event.body);
        this.isLoading = false;
        this.showSuccessNotification();
        //  this.receiver?.getItems();
      }
    },
    error: (error) => {
      console.error('File upload failed:', error);
      this.isLoading = false;
      this.showErrorNotification();
    },
    complete: () => {
      this.isLoading = false;
    },
  };

  constructor(public service: S, elastic?: boolean, downloadLogService?: DownloadLogService,  payload?: IPayload) {
    this.isElastic = elastic != undefined;
    this.downloadLogService = downloadLogService || null;
    this.filter = payload;
    this.createFileInput();
  }

  private createFileInput() {
    this.fileInput = document.createElement('input');
    this.fileInput.type = 'file';
    this.fileInput.accept = '.xlsx,.xls,.csv';
    this.fileInput.style.display = 'none';
    this.fileInput.addEventListener('change', (event: Event) => this.handleFileSelect(event));
    document.body.appendChild(this.fileInput);
  }

  private handleFileSelect(event: Event) {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      const file = input.files[0];
      this.uploadFile(file);
    }
  }

  private uploadFile(file: File) {
    this.isLoading = true;
    const additionalData = new Map<string, string>();
    if (this.filter) {
      additionalData.set('filter', JSON.stringify(this.filter));
    }

    this.service.upload(file, additionalData).subscribe(this.observer);

    // Reset file input for next upload
    if (this.fileInput) {
      this.fileInput.value = '';
    }
  }

  execute(el: T) {
    // Trigger file input click
    if (this.fileInput) {
      this.fileInput.click();
    }
  }

  setReceiver(receiver: RecordsManager<T>) {
    this.receiver = receiver;
  }

  isDisabled(el: T) {
    return this.receiver?.isLoading || this.isLoading || el?.isDisabled || false;
  }

  private showSuccessNotification() {
    if (this.downloadLogService) {
      this.downloadLogService.openSimpleSnackBar('Upload completed successfully');
    }
  }

  private showErrorNotification() {
    if (this.downloadLogService) {
      const errorMessage = 'Upload failed. Please try again.';
      this.downloadLogService.openSimpleSnackBar(errorMessage, 7000, ['error-snackbar']);
    }
  }
}
